/**
 * TIMEZONE CONVERTERS
 * ===================
 *
 * This module provides helper functions to handle timezone conversions between:
 * - Browser's local timezone (used by native Date, DatePicker, TimePicker)
 * - Courier's business timezone (set globally via moment.tz.setDefault in AuthProvider)
 *
 * WHY THIS EXISTS:
 * ----------------
 * JavaScript's native Date object ALWAYS operates in the browser's local timezone.
 * There's no way to change this globally (unlike moment.tz.setDefault).
 *
 * Our app sets moment.tz.setDefault() to the courier's timezone for business logic.
 * This creates a mismatch: UI components use browser TZ, but moment() uses courier TZ.
 *
 * Without these helpers, dates can shift by a day when:
 * - User in Tokyo selects Dec 10 → moment interprets as Dec 10 in NY → sends Dec 9 to server
 *
 * SOLUTION:
 * ---------
 * Use native Date methods (getFullYear, getMonth, getDate, getHours, getMinutes)
 * to extract values in browser's timezone, bypassing moment's global timezone.
 *
 * USAGE GUIDE:
 * ------------
 * | Scenario                          | Function                      |
 * |-----------------------------------|-------------------------------|
 * | DatePicker → Server (ISO string)  | datePickerDateToISODate()     |
 * | Server (ISO string) → DatePicker  | isoDateToDatePickerDate()     |
 * | TimePicker → Display ("HH:mm")    | dateToTimeString()            |
 * | Display ("HH:mm") → TimePicker    | timeStringToDate()            |
 * | Courier's "today" for defaults    | getCourierTodayForDatePicker()|
 * | Courier time → TimePicker Date    | momentToTimePickerDate()      |
 * | TimePicker Date → Courier moment  | timePickerDateToMoment()      |
 */

import moment from "moment-timezone";

/**
 * Convert moment (in courier timezone) to TimePicker-friendly Date (in browser timezone)
 * This preserves the visual time - e.g., 4:00 AM in courier TZ displays as 4:00 AM in browser
 *
 * Also sets the date to match the courier's date for completeness. This handles edge cases
 * where browser and courier are in different calendar days.
 *
 * @param m - Moment object in courier's timezone
 * @returns Date object that will display the same visual date-time in browser's timezone
 */
export function momentToTimePickerDate(m: moment.Moment): Date {
  const browserDate = new Date();
  // Set date from courier timezone
  browserDate.setFullYear(m.year(), m.month(), m.date());
  // Set time from courier timezone
  browserDate.setHours(m.hour(), m.minute(), m.second(), 0);
  return browserDate;
}

/**
 * Convert TimePicker Date (in browser timezone) to moment in courier timezone
 * This preserves the user's visual selection - e.g., user selects 4:00 AM → stored as 4:00 AM in courier TZ
 *
 * Preserves both date and time from the input Date. This is important because:
 * - BaseUI TimePicker preserves the date from its `value` prop when user changes time
 * - Native <input type="time"> gives today's date, which is also handled correctly
 *
 * @param browserDate - Date object from TimePicker (in browser's timezone)
 * @returns Moment object representing the same visual date-time in courier's timezone
 */
export function timePickerDateToMoment(browserDate: Date): moment.Moment {
  return moment()
    .year(browserDate.getFullYear())
    .month(browserDate.getMonth())
    .date(browserDate.getDate())
    .hour(browserDate.getHours())
    .minute(browserDate.getMinutes())
    .second(browserDate.getSeconds());
}

/**
 * Convert ISO date string from server to DatePicker-friendly Date object
 *
 * IMPORTANT: Cannot use moment() here because moment.tz.setDefault() is set globally
 * to the courier's timezone in AuthProvider. This would cause date shifts when the
 * browser timezone differs from the courier's timezone.
 *
 * Example bug scenario:
 * - Server sends "2025-12-08"
 * - Courier timezone is New York (EST)
 * - User browser is in Europe (CET)
 * - Using moment("2025-12-08") would interpret in EST, potentially causing shifts
 *
 * Solution: Parse ISO date directly using native Date constructor
 *
 * @param isoDateString - ISO date string from server (e.g., "2025-12-10")
 * @returns Date object that displays the same calendar date in browser's timezone
 *
 * @example
 * // Server sends "2025-12-08"
 * isoDateToDatePickerDate("2025-12-08") // Returns Date for Dec 8 in browser's local timezone
 */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function isoDateToDatePickerDate(isoDateString: string): Date {
  // Validate ISO date format
  if (!ISO_DATE_REGEX.test(isoDateString)) {
    throw new Error(
      `Invalid ISO date format: "${isoDateString}". Expected YYYY-MM-DD.`
    );
  }
  // Parse ISO date directly without moment to avoid timezone interpretation
  const [year, month, day] = isoDateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Convert Unix timestamp to DatePicker-friendly Date object (in courier timezone)
 *
 * Formats the timestamp as a calendar date in the courier's timezone (via
 * moment.tz.setDefault), then converts that ISO date string to a DatePicker
 * Date. This preserves the courier's calendar day regardless of browser TZ.
 *
 * @param unixTimestamp - Unix timestamp in seconds
 * @returns Date object for DatePicker representing the courier's calendar day
 */
export function unixToDatePickerDate(unixTimestamp: number): Date {
  return isoDateToDatePickerDate(moment.unix(unixTimestamp).format("YYYY-MM-DD"));
}

/**
 * Convert DatePicker Date object to ISO date string for server
 *
 * IMPORTANT: Cannot use moment() here because moment.tz.setDefault() is set globally
 * to the courier's timezone in AuthProvider. This would cause date shifts when the
 * browser timezone differs from the courier's timezone.
 *
 * Example bug scenario:
 * - User in Europe (CET) selects December 10
 * - Courier timezone is New York (EST)
 * - Using moment(date).format() would interpret date in EST, shifting to December 9
 *
 * Solution: Use native Date methods which always return values in browser's local timezone
 *
 * @param date - Date object from DatePicker (in browser's local timezone)
 * @returns ISO date string (YYYY-MM-DD format) preserving the selected calendar date
 *
 * @example
 * // User selects December 10, 2025 in DatePicker
 * datePickerDateToISODate(date) // Returns "2025-12-10" regardless of courier timezone
 */
export function datePickerDateToISODate(date: Date): string {
  // Use native Date methods to stay in browser's local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get the current date in the courier's timezone for DatePicker default value
 *
 * CONTEXT: moment.tz.setDefault() is set globally to the courier's timezone in AuthProvider.
 * This means moment() returns the current time in the COURIER's timezone, not the browser's.
 *
 * USE CASE: When initializing form default values, we want "today" to be in the courier's
 * timezone because that's the operational context. For example:
 * - User in Japan (JST, Dec 11 at 2:00 AM)
 * - Courier in New York (EST, Dec 10 at 11:00 AM)
 * - Default date should be Dec 10 (courier's "today"), not Dec 11 (user's "today")
 *
 * SOLUTION: Use moment() to get courier's current date, format as YYYY/MM/DD string,
 * then create a Date object that DatePicker can display.
 *
 * @returns Date object representing "today" in the courier's timezone
 *
 * @example
 * // Courier in NY (EST), User in Tokyo (JST, 14 hours ahead)
 * // When it's Dec 11 2:00 AM in Tokyo, it's Dec 10 11:00 AM in NY
 * getCourierTodayForDatePicker() // Returns Date for Dec 10 (courier's today)
 */
export function getCourierTodayForDatePicker(): Date {
  // Get current date in courier's timezone (via moment.tz.setDefault)
  const courierToday = moment().format("YYYY/MM/DD");
  // Create Date object for DatePicker (preserves the calendar date)
  return new Date(courierToday);
}

/**
 * Get default invoice date range: last 3 months through tomorrow (in courier timezone)
 *
 * @returns { startDate: Date, endDate: Date } for DatePicker
 */
export function getDefaultInvoiceDateRange(): {
  startDate: Date;
  endDate: Date;
} {
  const startDate = new Date(
    moment().subtract(3, "months").format("YYYY/MM/DD")
  );
  const endDate = new Date(moment().add(1, "days").format("YYYY/MM/DD"));
  return { startDate, endDate };
}

/**
 * Check if a DatePicker Date represents "today" in the courier's timezone
 *
 * IMPORTANT: Cannot use moment().isSame(date, "day") because moment interprets
 * the Date's UTC timestamp in courier's timezone, causing incorrect comparisons.
 *
 * @param input - Date object from DatePicker (in browser's local timezone)
 * @returns true if the date's calendar day matches courier's current day
 */
export function isCourierToday(input: Date | undefined): boolean {
  if (!input) return false;
  // Compare calendar dates as strings to avoid timezone interpretation issues
  const inputDateString = datePickerDateToISODate(input);
  const courierTodayString = moment().format("YYYY-MM-DD");
  return inputDateString === courierTodayString;
}

/**
 * Convert TimePicker time string ("HH:mm") to Date object
 *
 * Creates a Date in browser's local timezone with the specified time.
 * This ensures getHours()/getMinutes() returns the correct values when
 * the Date is later processed by makeTimeOnDate().
 *
 * @param timeString - Time string in "HH:mm" format (e.g., "14:30")
 * @returns Date object with the specified time in browser's local timezone
 *
 * @example
 * timeStringToDate("14:30") // Returns Date with hours=14, minutes=30 in browser TZ
 */
export function timeStringToDate(timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Convert Date object to TimePicker time string ("HH:mm")
 *
 * Reads hours/minutes using native Date methods (browser's local timezone).
 * This ensures consistency with timeStringToDate() which also uses browser TZ.
 *
 * @param date - Date object (or null/undefined)
 * @returns Time string in "HH:mm" format, or empty string if date is invalid
 *
 * @example
 * dateToTimeString(new Date()) // Returns current time like "14:30"
 * dateToTimeString(null) // Returns ""
 */
export function dateToTimeString(date: Date | null | undefined): string {
  if (!date || !(date instanceof Date)) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Combine visual time and date to create Unix timestamp in COURIER timezone
 *
 * PROBLEM: When user enters "5:41 PM" in TimePicker, they mean 5:41 PM in the
 * courier's timezone (the operational context), not their browser's timezone.
 *
 * EXAMPLE:
 * - User in Tokyo (Dec 3, 7:41 AM JST) enters "17:41" (5:41 PM)
 * - They mean 5:41 PM in New York (courier timezone)
 * - Expected UTC: Dec 2, 22:41 UTC (5:41 PM EST + 5 hours)
 * - Without this fix: Dec 2, 08:41 UTC (5:41 PM JST - 9 hours) ❌
 *
 * SOLUTION: Extract visual hour/minute from browser Date, extract visual
 * year/month/day from shipment date, then create moment in courier timezone.
 *
 * @param time - Date object from TimePicker (visual time in browser TZ)
 * @param date - Date object from DatePicker (visual date in browser TZ)
 * @returns Unix timestamp (seconds) representing that time in courier timezone,
 *          or undefined if inputs are invalid
 */
export function makeTimeOnDateInCourierTZ(
  time: Date | null | undefined,
  date: Date | undefined
): number | undefined {
  // Handle invalid inputs
  if (!time || !date) return undefined;

  // Extract visual time from browser Date
  const hours = time.getHours();
  const minutes = time.getMinutes();

  // Extract visual date from browser Date
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create moment in courier timezone (set via moment.tz.setDefault in AuthProvider)
  // moment() uses the default timezone, so setting year/month/date/hour/minute
  // creates the timestamp for that visual time in courier's timezone
  const courierMoment = moment()
    .year(year)
    .month(month)
    .date(day)
    .hour(hours)
    .minute(minutes)
    .second(0);

  return courierMoment.unix();
}

/**
 * Create Unix timestamp from DatePicker date and optional time string
 *
 * This is the SAFE way to create timestamps from DatePicker dates.
 * Handles the timezone conversion internally so developers don't need
 * to understand the browser TZ vs courier TZ complexity.
 *
 * HOW IT WORKS:
 * 1. Extracts the visual calendar date from DatePicker (browser TZ)
 * 2. Combines with time string (or current time if not provided)
 * 3. Parses the combined string in courier's timezone
 * 4. Returns Unix timestamp
 *
 * @param datePickerDate - Date object from DatePicker (visual date in browser TZ)
 * @param timeString - Optional time string like "14:30" or "HH:mm:ss"
 *                     If not provided, uses current time in courier TZ
 * @returns Unix timestamp (seconds) in courier timezone
 *
 * @example
 * // User selects Dec 15 in DatePicker, enters "14:30" time
 * datePickerToUnixTimestamp(dateFilter, "14:30")
 * // Returns Unix timestamp for Dec 15 2:30 PM in courier's timezone
 *
 * // Without time - uses current time on that date
 * datePickerToUnixTimestamp(dateFilter)
 * // Returns Unix timestamp for Dec 15 [current time] in courier's timezone
 */
export function datePickerToUnixTimestamp(
  datePickerDate: Date,
  timeString?: string
): number {
  const dateStr = datePickerDateToISODate(datePickerDate);
  const time = timeString || moment().format("HH:mm:ss");
  return moment(`${dateStr} ${time}`).unix();
}

/**
 * Build the force-complete override timestamp from a separate date and time.
 *
 * Returns unix seconds ONLY when BOTH date and time are provided; otherwise null
 * (meaning: no override). This is the single home for the "both-or-nothing" rule
 * shared by the single and bulk force-complete modals.
 *
 * @param date - Date object from DatePicker (visual date in browser TZ)
 * @param time - Date object from TimePicker (visual time in browser TZ)
 * @returns Unix timestamp (seconds) in the courier's timezone, or null
 */
export function buildCompleteAtOverride(
  date: Date | null,
  time: Date | null
): number | null {
  if (!date || !time) return null;
  return makeTimeOnDateInCourierTZ(time, date) ?? null;
}
